/* eslint-disable react/jsx-no-comment-textnodes */
import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import CancelIcon from '@material-ui/icons/Cancel';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import ScheduleIcon from '@material-ui/icons/Schedule';
import IconButton from '@material-ui/core/IconButton';
import Grid from '@material-ui/core/Grid';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import PhoneIcon from '@material-ui/icons/Phone';
import NoteAddIcon from '@material-ui/icons/NoteAdd';

import LoadingPageText from '../../../common/Loading/LoadingPageText';
import PatientDetails from '../../ClinicianView/AssignmentDetails/PatientDetails';
import TreatmentDetails from './TreatmentDetails';
import ActivityDetails from '../../ClinicianView/AssignmentDetails/ActivityDetails';
import CancelTreatment from '../../ClinicianView/CancelTreatment/CancelTreatment';
import RescheduleTreatment from '../../ClinicianView/UnassignedTreatments/RescheduleTreatment';
import ChangeTreatmentUnit from '../../ClinicianView/UnassignedTreatments/ChangeTreatmentUnit';
import ChangeTreatmentNotes from '../ChangeTreatmentNotes/ChangeTreatmentNotes';
import ConvertTreatmentInstanceType from '../ConvertTreatmentInstanceType/ConvertTreatmentInstanceType';

import useTimeline from '../../timeline/useTimeline';
import globals from '../../../../globals/communityGlobals';
import { useFetchUnassignedVisitDetails } from '../../../../actions/areas/planner';
import { parseDateUtc, isDateInPast } from '../../../../utils/dates';
import { checkFeatureAccess } from '../../../../utils/helpers';
import { removeOverrideTreatmentInstance } from '../../../../actions/entities';
import { getSchedulerPeriod } from '../../timeline/timelineHelper';
import { VisitType, AuditTrailType } from '../../../../enums';
import { cardTypes } from '../../../../settings/constants/cardTypes';
import AuditTrail from '../../../common/AuditTrail/AuditTrail';
import EditActivitiesDuration from '../../BundleVisit/EditActivitiesDuration';
import BundleVisitActivities from '../../BundleVisit/BundleVisitActivities';
import UnbundleTreatment from '../../BundleVisit/UnbundleTreatment';
import { TreatmentInstanceOccurrenceType } from '../../../../enums/treatmentInstanceOccurrenceType';

const locale = globals.getArea('treatments');
const dailyPlanningLocale = globals.getArea('dailyPlanning');

// eslint-disable-next-line react/prop-types
export function ClinicianTreatmentDetails({
  refreshGrid,
  includeTreatments,
  period,
  isBundled,
  forceRefreshClinicianTreatments,
}) {
  const faList = useSelector(store => store.global.auth.featureAccessList);

  const dispatch = useDispatch();
  const [state, setState] = useState({ forceRefresh: false, tab: 1, copyButtonDisabled: false });
  const selectedDate = useSelector(selState => selState.areas.dailyPlanning.selectedDate);
  const isNightMode = useSelector(selState => selState.areas.dailyPlanning.scheduler.isNightMode);
  const [showEditDurationPanel, setEditDurationPanel] = useState(false);
  const [overiddenDuration, setOveriddenDuration] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [showEditDurationButton, setEditDurationButton] = useState(false);
  const [showUnbundlePanel, setShowUnbundlePanel] = useState(false);

  const {
    activeUnassignedTreatment: assignment,
    setActiveUnassignedTreatment,
    activeUnassignedTreatmentForCancel,
    activeUnassignedTreatmentForReschedule,
    activeUnassignedTreatmentForChangeUnit,
    setActiveUnassignedTreatmentForCancel,
    setActiveUnassignedTreatmentForReschedule,
    setActiveUnassignedTreatmentForChangeUnit,
    setFlag,
    startLiveTrackingInterval,
    stopLiveTrackingInterval,
    refreshClinicianTreatmentsAndClosePanels,
    setActiveTreatmentInstanceForCopy,
    setAlertMessage,
    getFlag,
    getAccessToken,
  } = useTimeline();

  if (!assignment) return null;

  const isTreatmentInstanceCancelled = assignment.isCancelled;

  const { data: treatmentDetails, isDataLoading, error } = useFetchUnassignedVisitDetails(
    assignment.treatmentInstanceId,
    assignment.treatmentId,
    assignment.timeSlotId,
    selectedDate,
    state.forceRefresh,
  );

  useEffect(() => {
    stopLiveTrackingInterval();
    return () => startLiveTrackingInterval();
  }, []);

  useEffect(() => {
    if (treatmentDetails) {
      if (isBundled) {
        setEditDurationButton(treatmentDetails.treatmentInstance.cardType === cardTypes.MASTER);
      }
      // eslint-disable-next-line no-use-before-define
      if (isBundled || isConvertedVisit) {
        setOveriddenDuration(treatmentDetails.treatmentInstance.overriddenDuration);
      }
    }

    if (error) setAlertMessage({ message: error });
  }, [isDataLoading]);

  const canAllocateWork = checkFeatureAccess(faList, globals.applicationFeatureAccesses.canAllocateWork);

  const currentDay = globals.now;
  const schedulerPeriod = getSchedulerPeriod(period);
  const dayStartHour = isNightMode ? schedulerPeriod.endHour : schedulerPeriod.startHour;
  const virtualDate =
    currentDay.hour() < dayStartHour ? currentDay.startOf('day').add(-1, 'days') : currentDay.startOf('day');

  const isBehindNow = treatmentDetails
    ? parseDateUtc(treatmentDetails.treatmentInstance.occurrence).isBefore(parseDateUtc(virtualDate), 'day')
    : true;

  const deleteCopy = () => {
    dispatch(removeOverrideTreatmentInstance(assignment.nextCopyForDeletionId)).then(() => {
      refreshClinicianTreatmentsAndClosePanels();
      if (includeTreatments) refreshGrid();
    });
  };

  const copyTreatment = () => {
    setActiveTreatmentInstanceForCopy(treatmentDetails.treatmentInstance);
  };

  const isConvertedVisit =
    treatmentDetails &&
    treatmentDetails.treatmentInstance.occurrenceType === TreatmentInstanceOccurrenceType.ConvertedFromOnSiteVisit;

  const isSpecific = assignment.isSingleTimeSlot && assignment.treatmentTimeType === 'SpecificTime';
  const containerClass = classNames('sidePanel-header', {
    'card-status-red': isSpecific,
    'card-status-amber': assignment.isSingleTimeSlot && !isSpecific,
    'card-status-green': !assignment.isSingleTimeSlot,
    'is-copy': assignment.masterId,
  });

  const postcodeClass = classNames('info-text', {
    'is-treatment-address': treatmentDetails !== null && treatmentDetails.treatment.address,
  });

  const shouldRenderUnbundleBtn = () => {
    return canAllocateWork && isBundled && assignment.masterId == null && !isDateInPast(selectedDate);
  };

  const visitCanBeConverted = () => {
    return (
      !isDateInPast(selectedDate) &&
      (treatmentDetails.treatmentInstance.cardType === cardTypes.MASTER ||
        treatmentDetails.treatmentInstance.cardType === cardTypes.REVIEW) &&
      assignment.visitType === VisitType.OnSite &&
      !isBundled &&
      assignment.additionalClinician === 0 &&
      assignment.numberOfClinicians === 1
    );
  };

  const headerRender = () => {
    const activeDeactiveBtnRender = () => {
      if (getFlag('enableMultipleSelection')) return null;

      return (
        <List component="nav" style={{ paddingTop: '0px', paddingBottom: '6px' }}>
          {canAllocateWork && !assignment.masterId && !isBundled && (
            <ListItem
              className="header-action-button"
              key={'cancel-btn'}
              button
              onClick={() => setActiveUnassignedTreatmentForCancel(assignment)}
            >
              <ListItemIcon>
                <RemoveCircleIcon />
              </ListItemIcon>
              <ListItemText primary={'Cancel visit'} />
            </ListItem>
          )}
          {canAllocateWork && !isBehindNow && assignment.visitType !== VisitType.PhoneCall && (
            <ListItem
              className="header-action-button"
              key={'copy-btn'}
              button
              onClick={copyTreatment}
              disabled={state.copyButtonDisabled}
            >
              <ListItemIcon>
                <span className="icon-extend icon-copy" />
              </ListItemIcon>
              <ListItemText primary={'Copy visit'} />
            </ListItem>
          )}
          {canAllocateWork && assignment.nextCopyForDeletionId && (
            <ListItem className="header-action-button" key={'delete-btn'} button onClick={deleteCopy}>
              <ListItemIcon>
                <span className="icon-extend icon-delete" />
              </ListItemIcon>
              <ListItemText primary={'Delete Copy'} />
            </ListItem>
          )}
          {canAllocateWork && !isConvertedVisit && (
            <ListItem
              className="header-action-button"
              key={'change-unit-btn'}
              button
              onClick={() => setActiveUnassignedTreatmentForChangeUnit(assignment)}
            >
              <ListItemIcon>
                <span className="icon-extend icon-edit-mode" />
              </ListItemIcon>
              <ListItemText primary={'Change Unit'} />
            </ListItem>
          )}
          {canAllocateWork && !isBundled && !isConvertedVisit && (
            <ListItem
              className="header-action-button"
              key={'reschedule-btn'}
              button
              onClick={() => setActiveUnassignedTreatmentForReschedule(assignment)}
            >
              <ListItemIcon>
                <span className="icon-extend icon-reschedule" />
              </ListItemIcon>
              <ListItemText primary={'Reschedule'} />
            </ListItem>
          )}
          {canAllocateWork && showEditDurationButton && (
            <ListItem
              className="header-action-button"
              key={'edit-total-duration-btn'}
              button
              onClick={() => setEditDurationPanel(true)}
            >
              <ListItemIcon>
                <span className="icon-extend icon-edit-mode" />
              </ListItemIcon>
              <ListItemText primary={locale.getMsg('editDuration')} />
            </ListItem>
          )}
          {shouldRenderUnbundleBtn() && (
            <ListItem
              className="header-action-button"
              key={'unbundle-btn'}
              button
              onClick={() => setShowUnbundlePanel(true)}
            >
              <ListItemIcon>
                <span className="icon-extend icon-unbundle-visits" />
              </ListItemIcon>
              <ListItemText primary={dailyPlanningLocale.getMsg('unbundle')} />
            </ListItem>
          )}
          {canAllocateWork && !assignment.masterId && (
            <ListItem
              className="header-action-button"
              key={'add-notes-btn'}
              button
              onClick={() => setFlag('showTreatmentNotesSidePanel', true)}
            >
              <ListItemIcon>
                <NoteAddIcon />
              </ListItemIcon>
              <ListItemText primary={'Add Notes'} />
            </ListItem>
          )}
          {canAllocateWork && treatmentDetails && visitCanBeConverted() && (
            <ListItem
              className="header-action-button"
              key={'convert-visit-btn'}
              button
              onClick={() => setFlag('showConvertVisitSidePanel', true)}
            >
              <ListItemIcon>
                <span className="icon-convert-visit-call icon-svg asw-icon" />
              </ListItemIcon>
              <ListItemText primary={'Convert Visit'} />
            </ListItem>
          )}
        </List>
      );
    };
    return (
      <div className={containerClass}>
        <div className="sidePanel-header-subtitle">
          <Box display="flex">
            <Box flexGrow={1} py={1}>
              <Typography variant="subtitle1">
                {isBundled ? locale.getMsg('unassignedBundleVisit') : locale.getMsg('unassignedVisit')}
                {assignment.treatmentInstanceId && <span> #{assignment.treatmentInstanceId}</span>}
              </Typography>
            </Box>
            <Box>
              <IconButton onClick={() => setActiveUnassignedTreatment(null)}>
                <CancelIcon />
              </IconButton>
            </Box>
          </Box>
        </div>
        <div className="sidePanel-header-wrapper">
          <div className="sidePanel-header-left-info-wrap">
            <div className="sidePanel-header-title">
              <Typography variant="h2" gutterBottom>
                {assignment.patientName}
                <span
                  className={`icon-rag-${assignment.ragStatus.name.toLowerCase()} icon-svg asw-icon rag-status-icon`}
                />
              </Typography>
              <Typography variant="h5">{assignment.band}</Typography>
            </div>
            <div className="sidePanel-header-info patient-header">
              <Box className="assignment-desc-info" display="flex" flexDirection="row">
                <Box display="flex" flexDirection="column">
                  {assignment.visitType === VisitType.PhoneCall ? (
                    <Box className="header-icon-wrapper">
                      <PhoneIcon fontSize="small" />
                      {assignment.contactNumber}
                    </Box>
                  ) : (
                    <Box className="header-icon-wrapper">
                      <LocationOnIcon />
                      <span className={postcodeClass}>{assignment.postcode || ''}</span>
                    </Box>
                  )}
                  <Box className="header-icon-wrapper" display="flex" flexDirection="row">
                    <ScheduleIcon />
                    <span className="info-text">{`${
                      (isBundled || isConvertedVisit) && overiddenDuration !== null
                        ? overiddenDuration
                        : assignment.totalTime
                    } min`}</span>
                  </Box>
                  <Box display="flex" alignItems="center">
                    {((treatmentDetails &&
                      treatmentDetails.treatment.preferredClinicianId &&
                      !treatmentDetails.treatmentInstance.masterId === null) ||
                      (assignment && assignment.preferredClinicianId && assignment.masterId === null)) && (
                      <Box className="header-icon-wrapper">
                        <span className="icon-extend icon-person-star icon-xsmall" />
                      </Box>
                    )}
                    {treatmentDetails && treatmentDetails.treatmentInstance.runsOvernight && (
                      <Box className="header-icon-wrapper">
                        <div className="night-shift-icon">
                          <span className="icon-extend icon-night" />
                        </div>
                      </Box>
                    )}
                    {treatmentDetails && isBundled && (
                      <Box className="header-icon-wrapper">
                        <span className="icon-extend icon-bundle-visits icon-small" />
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box display="flex" flexDirection="column">
                  <Box className="header-icon-wrapper">
                    <span className="icon-extend icon-multiple-times" />
                    <span className="info-text">
                      {!isDataLoading && treatmentDetails !== null && treatmentDetails.label}
                    </span>
                  </Box>
                  <Box className="header-icon-wrapper">
                    <span className="icon-extend icon-medic icon-small" />
                    <span className="info-text">
                      {assignment.additionalClinician > 0
                        ? `${assignment.numberOfClinicians - assignment.additionalClinician} + ${
                            assignment.additionalClinician
                          }`
                        : assignment.numberOfClinicians}
                    </span>
                  </Box>
                </Box>
                <Box display="flex" flexDirection="column">
                  {assignment.unassignSourceBySystem && (
                    <Box className="header-icon-wrapper icon-unassigned-wrapper">
                      <span className="icon-unassigned icon-svg asw-icon" />
                      <span className="info-text">{assignment.unassignSourceBySystem}</span>
                    </Box>
                  )}
                  {assignment.isReviewDate && (
                    <Box className="header-icon-wrapper">
                      <span className="icon-review icon-svg asw-icon review-date-icon" />
                    </Box>
                  )}
                </Box>
                {treatmentDetails && isConvertedVisit && (
                  <Box className="header-icon-wrapper">
                    <span className="icon-convert-visit-call icon-svg asw-icon" />
                    <span className="info-text">{locale.getMsg('converted')}</span>
                  </Box>
                )}
              </Box>
            </div>
          </div>
          {!isTreatmentInstanceCancelled && <div className="sidePanel-header-action">{activeDeactiveBtnRender()}</div>}
        </div>
      </div>
    );
  };

  const assignmentDetailsRender = () => {
    return (
      <React.Fragment>
        <div className="sidePanel-content">
          <Grid container spacing={0}>
            <Grid item xs={12} sm={12}>
              <Tabs
                className="custom-tabs"
                value={state.tab}
                onChange={(event, value) => setState({ tab: value })}
                centered
              >
                <Tab label={isBundled ? locale.getMsg('visits') : locale.getMsg('activities')} />
                <Tab label="Patient" />
                <Tab label="Treatment" style={{ display: !isBundled ? 'block' : 'none' }} />
                {assignment.treatmentInstanceId > 0 && !isTreatmentInstanceCancelled && <Tab label="Audit Trails" />}
              </Tabs>
            </Grid>
            <Grid item xs={12}>
              {state.tab === 0 &&
                (!isBundled ? (
                  <Box>
                    <ActivityDetails treatmentDetails={treatmentDetails} />
                  </Box>
                ) : (
                  <Box>
                    <span className="form-validation-error">
                      <p>{errorMsg}</p>
                    </span>
                    <BundleVisitActivities
                      items={treatmentDetails.treatmentInstance.originalTreatmentInstances}
                      treatmentInstance={treatmentDetails.treatmentInstance}
                      overiddenDuration={overiddenDuration}
                      errorMsg={setErrorMsg}
                      isBundled
                    />
                  </Box>
                ))}
              {state.tab === 1 && treatmentDetails !== null && (
                <Box>
                  <PatientDetails patientDetails={treatmentDetails.patient} />
                </Box>
              )}
              {state.tab === 2 && treatmentDetails !== null && !isBundled && (
                <Box>
                  <TreatmentDetails
                    treatmentInstance={treatmentDetails.treatmentInstance}
                    treatment={treatmentDetails.treatment}
                    lastVisitedInfo={{
                      clinicianName: treatmentDetails.lastVisitedClinicianFullName,
                      lastVisitedTime: treatmentDetails.lastVisitedTime,
                    }}
                    canEdit={!isTreatmentInstanceCancelled && !isConvertedVisit && canAllocateWork}
                    includeTreatments={includeTreatments}
                    refreshGrid={refreshGrid}
                  />
                </Box>
              )}
              {state.tab === 3 && assignment.treatmentInstanceId > 0 && !isTreatmentInstanceCancelled && (
                <Box>
                  <AuditTrail auditTrailType={AuditTrailType.Visit} entityId={assignment.treatmentInstanceId} />
                </Box>
              )}
            </Grid>
          </Grid>
        </div>
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      <section className="side-panel-content-wrapper">
        <SwipeableDrawer
          anchor="right"
          open
          onOpen={() => {}}
          onClose={() => {}}
          ModalProps={{
            onBackdropClick: () => setActiveUnassignedTreatment(null),
          }}
        >
          <div className="sidePanel-wrapper" style={{ width: '635px' }} id="unassigned-side-panel">
            {headerRender()}
            {!isDataLoading ? (
              assignmentDetailsRender()
            ) : (
              <LoadingPageText showHeader={false}>Wait a moment while we gather treatment info...</LoadingPageText>
            )}
          </div>
        </SwipeableDrawer>
      </section>
      {activeUnassignedTreatmentForCancel !== null && treatmentDetails.treatmentInstance !== null && (
        <CancelTreatment
          treatmentDetails={treatmentDetails}
          toggleRefresh={refreshGrid}
          includeTreatments={includeTreatments}
        />
      )}
      {activeUnassignedTreatmentForReschedule !== null && treatmentDetails && (
        <RescheduleTreatment
          treatmentDetails={treatmentDetails}
          toggleRefresh={refreshGrid}
          includeTreatments={includeTreatments}
          period={period}
        />
      )}
      {activeUnassignedTreatmentForChangeUnit !== null && treatmentDetails && (
        <ChangeTreatmentUnit
          treatmentDetails={treatmentDetails}
          toggleRefresh={refreshGrid}
          includeTreatments={includeTreatments}
        />
      )}
      {showEditDurationPanel && showEditDurationButton && (
        <EditActivitiesDuration
          openDurationPanel={setEditDurationPanel}
          onEditDuration={setOveriddenDuration}
          getAccessToken={getAccessToken}
          treatmentInstanceId={assignment.treatmentInstanceId}
          toggleRefresh={refreshGrid}
          refreshClinicianTreatmentsAndClosePanels={refreshClinicianTreatmentsAndClosePanels}
        />
      )}
      {showUnbundlePanel && (
        <UnbundleTreatment
          onSuccess={() => {
            setShowUnbundlePanel(false);
            forceRefreshClinicianTreatments();
            refreshClinicianTreatmentsAndClosePanels();
            if (includeTreatments) {
              refreshGrid();
            }
          }}
          onBack={() => setShowUnbundlePanel(false)}
          treatmentInstanceId={assignment.treatmentInstanceId}
        />
      )}
      {getFlag('showTreatmentNotesSidePanel') === true && treatmentDetails && (
        <ChangeTreatmentNotes
          treatmentDetails={treatmentDetails}
          toggleRefresh={refreshGrid}
          includeTreatments={includeTreatments}
        />
      )}
      {getFlag('showConvertVisitSidePanel') === true && treatmentDetails && (
        <ConvertTreatmentInstanceType treatmentDetails={treatmentDetails} toggleRefresh={refreshGrid} />
      )}
    </React.Fragment>
  );
}
